import { toast } from 'react-toastify';

import type { AnyAction } from '@reduxjs/toolkit';
import type { FulfilledAction, IMetaState, PendingAction, RejectedAction } from '@/store/types';
import type { WritableDraft } from 'immer/dist/internal';

import i18n from '@/config/i18n';
import { SLICES } from '@/constants/actions';

const isRejectedAction = (action: AnyAction): action is RejectedAction => {
  return action.type.endsWith('/rejected');
};

const isPendingAction = (action: AnyAction): action is PendingAction => {
  return action.type.endsWith('/pending');
};

const isFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  return action.type.endsWith('/fulfilled');
};

const isActionFromSlice = (action: AnyAction, slice: SLICES): boolean => {
  return !!action.type.match(new RegExp(`^((su|sp)\\/)?${slice}`));
};

const _getActionTypePrefix = (action: AnyAction): string => action.type.replace(/\/(pending|rejected|fulfilled)/, '');

const onPendingAction = <T extends IMetaState>(state: WritableDraft<T>, action: AnyAction): void => {
  const typePrefix = _getActionTypePrefix(action);
  if (!state.meta.pending.includes(typePrefix)) {
    state.meta.pending.push(typePrefix);
  }
};

const onRejectedAction = <T extends IMetaState>(state: WritableDraft<T>, action: AnyAction): void => {
  const typePrefix = _getActionTypePrefix(action);
  state.meta.pending = state.meta.pending.filter(type => type !== typePrefix);

  if (!action.meta.aborted) {
    toast.error(i18n.t('common.somethingWentWrong'));
  }
};

const onFulfilledAction = <T extends IMetaState>(state: WritableDraft<T>, action: AnyAction): void => {
  const typePrefix = _getActionTypePrefix(action);
  state.meta.pending = state.meta.pending.filter(type => type !== typePrefix);
};

const getActionPrefixes = (slice: SLICES): { suPrefix: string; spPrefix: string; defaultPrefix: string } => {
  return {
    suPrefix: `su/${slice}`,
    spPrefix: `sp/${slice}`,
    defaultPrefix: `${slice}`,
  };
};

export {
  getActionPrefixes,
  isRejectedAction,
  isPendingAction,
  isFulfilledAction,
  isActionFromSlice,
  onPendingAction,
  onRejectedAction,
  onFulfilledAction,
};
